import React, { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  inventoryGridRowSx,
  inventoryStickyLabelCellSx,
  inventoryMobileGridCornerSx,
} from '../../utils/inventoryMobileLayout';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';

const shimmerSweep = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

export function MobileInventoryGridCorner({ isDark, label = 'Day' }) {
  return (
    <Box sx={inventoryMobileGridCornerSx(isDark)}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.5rem',
          fontWeight: 700,
          color: DASHBOARD_ACCENT,
          lineHeight: 1,
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function skeletonCellBg(isDark) {
  return isDark
    ? `linear-gradient(90deg, ${alpha('#fff', 0.04)} 0%, ${alpha(DASHBOARD_ACCENT, 0.1)} 45%, ${alpha('#fff', 0.04)} 90%)`
    : `linear-gradient(90deg, ${alpha('#000', 0.04)} 0%, ${alpha(DASHBOARD_ACCENT, 0.12)} 45%, ${alpha('#000', 0.04)} 90%)`;
}

/**
 * Single skeleton row matching inventory/rate channel or room-type rows.
 */
export function MobileInventorySkeletonRow({
  isMobile,
  dates,
  isDark,
  theme,
  desktopLabelWidth = 200,
  variant = 'channel', // 'channel' | 'combined' | 'roomType'
  staggerIndex = 0,
  showExpandHint = false,
  expandOpen = false,
}) {
  const isRoomType = variant === 'roomType';
  const labelHeight = isMobile
    ? 42
    : (isRoomType ? 40 : 56);
  const cellHeight = labelHeight;
  const delayMs = Math.min(staggerIndex, 10) * 45;

  return (
    <Box
      sx={{
        ...inventoryGridRowSx(isMobile, dates.length, desktopLabelWidth, {
          mb: isRoomType ? 0.5 : 1,
        }),
        opacity: 0,
        animation: 'invSkelFadeIn 0.35s ease forwards',
        animationDelay: `${delayMs}ms`,
        '@keyframes invSkelFadeIn': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          ...inventoryStickyLabelCellSx(isMobile, isDark),
          minHeight: labelHeight,
          pl: isRoomType ? { xs: 1.25, sm: 2 } : undefined,
          borderLeft: isRoomType && !isMobile
            ? `3px solid ${alpha(DASHBOARD_ACCENT, 0.35)}`
            : undefined,
          borderBottom: expandOpen && !isRoomType
            ? `2px solid ${DASHBOARD_ACCENT}`
            : undefined,
          backgroundColor: expandOpen && !isRoomType
            ? (isDark ? alpha(DASHBOARD_ACCENT, 0.1) : alpha(DASHBOARD_ACCENT, 0.07))
            : undefined,
          gap: 1,
        }}
      >
        {showExpandHint && !isMobile && !isRoomType && (
          <Box
            sx={{
              width: 14,
              height: 14,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: DASHBOARD_ACCENT,
              opacity: 0.7,
              transform: expandOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ▾
          </Box>
        )}
        {!isRoomType && (
          <Skeleton
            variant="rounded"
            width={isMobile ? 22 : 28}
            height={isMobile ? 22 : 28}
            animation="wave"
            sx={{ flexShrink: 0, borderRadius: 1 }}
          />
        )}
        {!isMobile && (
          <Skeleton
            variant="rounded"
            width={variant === 'combined' ? 96 : isRoomType ? 88 : 72}
            height={isRoomType ? 14 : 16}
            animation="wave"
            sx={{ borderRadius: 1, flexShrink: 0 }}
          />
        )}
        {!isMobile && !isRoomType && (
          <Skeleton
            variant="rounded"
            width={22}
            height={18}
            animation="wave"
            sx={{
              ml: 'auto',
              borderRadius: 10,
              bgcolor: alpha(DASHBOARD_ACCENT, isDark ? 0.25 : 0.35),
            }}
          />
        )}
      </Box>
      {dates.map((date, cellIndex) => (
        <Box
          key={date.format('YYYY-MM-DD')}
          sx={{
            height: cellHeight,
            borderRadius: isMobile ? '8px' : 1,
            border: `1px solid ${
              isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06)
            }`,
            backgroundImage: skeletonCellBg(isDark),
            backgroundSize: '200% 100%',
            animation: `${shimmerSweep} 1.4s ease-in-out infinite`,
            animationDelay: `${delayMs + cellIndex * 18}ms`,
            opacity: 0.9,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Full grid skeleton that mirrors expected layout (combined vs channel vs listings).
 * Combined mode defaults to PMS collapsed + All Channels expanded (with room stubs).
 */
export function InventoryGridSkeleton({
  isMobile,
  dates,
  isDark,
  theme,
  desktopLabelWidth = 200,
  mode = 'combined', // 'combined' | 'channels' | 'listings'
  rowCount = null,
  withExpandedRooms = false,
  roomTypeCount = 3,
  /** Which combined-mode rows should show nested room stubs. Default: only All Channels. */
  expandedRowKeys = ['combined'],
}) {
  const rows = useMemo(() => {
    if (mode === 'combined') {
      return [
        { key: 'pms', variant: 'channel', showExpandHint: true, expandOpen: false },
        { key: 'combined', variant: 'combined', showExpandHint: true, expandOpen: true },
      ];
    }
    const count = Math.max(1, rowCount ?? (mode === 'listings' ? 4 : 6));
    return Array.from({ length: count }, (_, i) => ({
      key: `${mode}-${i}`,
      variant: 'channel',
      showExpandHint: mode === 'channels',
      expandOpen: false,
    }));
  }, [mode, rowCount]);

  const shouldShowRooms = (row) =>
    withExpandedRooms && expandedRowKeys.includes(row.key);

  return (
    <Box
      aria-busy="true"
      aria-label="Loading grid"
      sx={{ width: '100%' }}
    >
      {rows.map((row, index) => (
        <React.Fragment key={row.key}>
          <MobileInventorySkeletonRow
            isMobile={isMobile}
            dates={dates}
            isDark={isDark}
            theme={theme}
            desktopLabelWidth={desktopLabelWidth}
            variant={row.variant}
            staggerIndex={index}
            showExpandHint={row.showExpandHint}
            expandOpen={row.expandOpen}
          />
          {shouldShowRooms(row) && (
            <Box
              sx={{
                mb: 1,
                ml: { xs: 0, sm: 0.5 },
                pl: { xs: 0, sm: 0.5 },
                borderLeft: {
                  xs: 'none',
                  sm: `2px solid ${alpha(DASHBOARD_ACCENT, 0.25)}`,
                },
              }}
            >
              {Array.from({ length: Math.min(roomTypeCount, 6) }, (_, ri) => (
                <MobileInventorySkeletonRow
                  key={`${row.key}-rt-${ri}`}
                  isMobile={isMobile}
                  dates={dates}
                  isDark={isDark}
                  theme={theme}
                  desktopLabelWidth={desktopLabelWidth}
                  variant="roomType"
                  staggerIndex={index * 4 + ri + 1}
                />
              ))}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
